import { useEffect, useRef, useState } from 'react';

/**
 * KHQR renderer — the admin's copy, for paying US (§3 `billing`).
 *
 * Rendered locally rather than fetched from an image service: the payload
 * carries an account number and an exact amount, so handing it to a third
 * party would leak who is paying whom, and the merchant would be watching a
 * spinner on Cambodian mobile data at the exact moment they are deciding
 * whether to trust us with money.
 *
 * A near-copy of the storefront's. Sharing it would mean a package that exists
 * to hold one component and drags `qrcode` into both bundles regardless of
 * which one needs it; the duplication is thirty lines that will not change.
 */
export function QrCode({ payload, size = 260 }: { payload: string; size?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Lazy-loaded so the catalog pages never pay for the QR encoder.
    void import('qrcode').then(async (module) => {
      if (cancelled || !canvasRef.current) return;
      try {
        await module.default.toCanvas(canvasRef.current, payload, {
          width: size,
          margin: 1,
          errorCorrectionLevel: 'M',
          color: { dark: '#000000', light: '#ffffff' },
        });
      } catch {
        if (!cancelled) setFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [payload, size]);

  if (failed) {
    // Falling back to the raw payload still lets a customer pay: most bank
    // apps accept a pasted KHQR string.
    return (
      <code
        className="block w-full break-all rounded-xl p-3 text-[10px]"
        style={{ background: 'var(--color-surface)' }}
      >
        {payload}
      </code>
    );
  }

  return (
    <div className="flex justify-center">
      <canvas
        ref={canvasRef}
        width={size}
        height={size}
        className="rounded-xl bg-white p-2"
        style={{ width: size, height: size }}
      />
    </div>
  );
}
